/** @format */

import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Book } from "../types";

export const useBooks = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);

  // Fetch rating and review count for a book
  const fetchBookRating = useCallback(
    async (
      bookId: string
    ): Promise<{ rating: number; reviewCount: number }> => {
      try {
        const reviewsQuery = query(
          collection(db, "BooksReviews"),
          where("bookId", "==", bookId)
        );

        const reviewsSnapshot = await getDocs(reviewsQuery);
        const reviews = reviewsSnapshot.docs.map((doc) => doc.data());

        if (reviews.length === 0) {
          return { rating: 0, reviewCount: 0 };
        }

        const totalRating = reviews.reduce(
          (sum, review) => sum + (review.rating || 0),
          0
        );
        const averageRating = totalRating / reviews.length;

        return {
          rating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
          reviewCount: reviews.length,
        };
      } catch (err) {
        console.error("Error fetching book rating:", err);
        return { rating: 0, reviewCount: 0 };
      }
    },
    []
  );

  // Fetch books from Firestore
  const fetchBooks = useCallback(
    async (filters?: {
      category?: string;
      program?: string;
      priceRange?: { min: number; max: number };
      search?: string;
      userProgram?: string;
    }) => {
      try {
        setLoading(true);
        setError(null);

        // Start with basic query - don't order by createdAt initially since it might not exist
        let q = query(collection(db, "books"));

        // Apply filters
        if (filters?.category && filters.category !== "all") {
          q = query(q, where("category", "==", filters.category));
        }

        if (filters?.program && filters.program !== "all") {
          q = query(q, where("program", "==", filters.program));
        }

        // Filter by user program if specified (override the program filter)
        if (filters?.userProgram && filters.userProgram !== "all") {
          q = query(q, where("program", "==", filters.userProgram));
        }

        if (filters?.priceRange) {
          if (filters.priceRange.min > 0) {
            q = query(q, where("amount", ">=", filters.priceRange.min));
          }
          if (filters.priceRange.max > 0) {
            q = query(q, where("amount", "<=", filters.priceRange.max));
          }
        }

        // Limit results for pagination
        q = query(q, limit(12));

        const querySnapshot = await getDocs(q);
        const booksData: Book[] = [];

        // Process books first (without ratings for faster initial load)
        for (const docSnapshot of querySnapshot.docs) {
          const bookData = docSnapshot.data();

          booksData.push({
            id: docSnapshot.id,
            title: bookData.title || "",
            author: bookData.author || "",
            description: bookData.description || "",
            category: bookData.category || "",
            program: bookData.program || "general",
            amount: bookData.amount || 0,
            coverImage: bookData.coverImage || bookData.image_url || "",
            pdf_url: bookData.pdf_url || "",
            createdAt:
              bookData.createdAt?.toDate() ||
              bookData.updated_at?.toDate() ||
              new Date(),
            updatedAt:
              bookData.updatedAt?.toDate() ||
              bookData.updated_at?.toDate() ||
              new Date(),
            rating: 0, // Set default rating
            reviewCount: 0, // Set default review count
          });
        }

        // Set books immediately for faster display
        setBooks(booksData);
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === 12);

        // Fetch ratings asynchronously after books are displayed
        setTimeout(async () => {
          const booksWithRatings = [...booksData];
          for (let i = 0; i < booksWithRatings.length; i++) {
            try {
              const { rating, reviewCount } = await fetchBookRating(
                booksWithRatings[i].id
              );
              booksWithRatings[i].rating = rating;
              booksWithRatings[i].reviewCount = reviewCount;
            } catch (err) {
              console.error(
                "Error fetching rating for book:",
                booksWithRatings[i].id,
                err
              );
            }
          }
          setBooks(booksWithRatings);
        }, 100); // Small delay to ensure books are displayed first
      } catch (err) {
        console.error("Error fetching books:", err);
        setError("Failed to load books");
      } finally {
        setLoading(false);
      }
    },
    []
  ); // Empty dependency array since it doesn't depend on any state

  // Load more books (pagination)
  const loadMoreBooks = useCallback(async () => {
    if (!hasMore || loading) return;

    try {
      setLoading(true);
      setError(null);

      let q = query(collection(db, "books"), limit(12));

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const additionalBooks: Book[] = [];

      // Process additional books first (without ratings for faster display)
      for (const docSnapshot of querySnapshot.docs) {
        const bookData = docSnapshot.data();

        additionalBooks.push({
          id: docSnapshot.id,
          title: bookData.title || "",
          author: bookData.author || "",
          description: bookData.description || "",
          category: bookData.category || "",
          program: bookData.program || "general",
          amount: bookData.amount || 0,
          coverImage: bookData.coverImage || bookData.image_url || "",
          pdf_url: bookData.pdf_url || "",
          createdAt:
            bookData.createdAt?.toDate() ||
            bookData.updated_at?.toDate() ||
            new Date(),
          updatedAt:
            bookData.updatedAt?.toDate() ||
            bookData.updated_at?.toDate() ||
            new Date(),
          rating: 0, // Set default rating
          reviewCount: 0, // Set default review count
        });
      }

      // Add books immediately for faster display
      setBooks((prev) => [...prev, ...additionalBooks]);
      setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(additionalBooks.length === 12);

      // Fetch ratings asynchronously after books are displayed
      setTimeout(async () => {
        const booksWithRatings = [...additionalBooks];
        for (let i = 0; i < booksWithRatings.length; i++) {
          try {
            const { rating, reviewCount } = await fetchBookRating(
              booksWithRatings[i].id
            );
            booksWithRatings[i].rating = rating;
            booksWithRatings[i].reviewCount = reviewCount;
          } catch (err) {
            console.error(
              "Error fetching rating for book:",
              booksWithRatings[i].id,
              err
            );
          }
        }

        // Update the books with ratings
        setBooks((prev) => {
          const updatedBooks = [...prev];
          const startIndex = updatedBooks.length - additionalBooks.length;
          for (let i = 0; i < booksWithRatings.length; i++) {
            updatedBooks[startIndex + i].rating = booksWithRatings[i].rating;
            updatedBooks[startIndex + i].reviewCount =
              booksWithRatings[i].reviewCount;
          }
          return updatedBooks;
        });
      }, 100);
    } catch (err) {
      console.error("Error loading more books:", err);
      setError("Failed to load more books");
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, lastDoc, fetchBookRating]);

  // Search books
  const searchBooks = useCallback(
    async (searchTerm: string) => {
      try {
        setLoading(true);
        setError(null);

        const trimmed = searchTerm.trim();
        if (!trimmed) {
          // If search is cleared, just refetch default set
          await fetchBooks();
          return;
        }

        // Attempt a prefix search on title to avoid scanning the entire collection.
        // NOTE: This is case-sensitive. For best results, maintain a lowercase `title_lower` field and change orderBy accordingly.
        let booksResult: Book[] = [];
        try {
          const prefixQuery = query(
            collection(db, "books"),
            orderBy("title"),
            startAt(trimmed),
            endAt(trimmed + "\uf8ff"),
            limit(100)
          );
          const snap = await getDocs(prefixQuery);
          const candidates = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as any[];

          // Client-side fallback filtering to match title/author/description (bounded to the 100 candidates above)
          const filtered = candidates.filter((book) => {
            const t = String(book.title || "").toLowerCase();
            const a = String(book.author || "").toLowerCase();
            const d = String(book.description || "").toLowerCase();
            const s = trimmed.toLowerCase();
            return t.includes(s) || a.includes(s) || d.includes(s);
          });

          // Fetch ratings for filtered books (bounded)
          for (const bookData of filtered) {
            const { rating, reviewCount } = await fetchBookRating(bookData.id);
            booksResult.push({
              id: bookData.id,
              title: bookData.title || "",
              author: bookData.author || "",
              description: bookData.description || "",
              category: bookData.category || "",
              program: (bookData.program as "ICAG" | "CITG" | "general") || "general",
              amount: bookData.amount || 0,
              coverImage: bookData.coverImage || bookData.image_url || "",
              pdf_url: bookData.pdf_url || "",
              createdAt:
                bookData.createdAt?.toDate?.() ||
                bookData.updated_at?.toDate?.() ||
                new Date(),
              updatedAt:
                bookData.updatedAt?.toDate?.() ||
                bookData.updated_at?.toDate?.() ||
                new Date(),
              rating,
              reviewCount,
            });
          }
        } catch (e) {
          // Index might be missing for orderBy("title"). Fall back to a bounded scan with client-side filter.
          const bounded = query(collection(db, "books"), limit(300));
          const snap = await getDocs(bounded);
          const all = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as any[];
          const filtered = all.filter((book) => {
            const t = String(book.title || "").toLowerCase();
            const a = String(book.author || "").toLowerCase();
            const d = String(book.description || "").toLowerCase();
            const s = trimmed.toLowerCase();
            return t.includes(s) || a.includes(s) || d.includes(s);
          }).slice(0, 100);

          for (const bookData of filtered) {
            const { rating, reviewCount } = await fetchBookRating(bookData.id);
            booksResult.push({
              id: bookData.id,
              title: bookData.title || "",
              author: bookData.author || "",
              description: bookData.description || "",
              category: bookData.category || "",
              program: (bookData.program as "ICAG" | "CITG" | "general") || "general",
              amount: bookData.amount || 0,
              coverImage: bookData.coverImage || bookData.image_url || "",
              pdf_url: bookData.pdf_url || "",
              createdAt:
                bookData.createdAt?.toDate?.() ||
                bookData.updated_at?.toDate?.() ||
                new Date(),
              updatedAt:
                bookData.updatedAt?.toDate?.() ||
                bookData.updated_at?.toDate?.() ||
                new Date(),
              rating,
              reviewCount,
            });
          }
        }

        setBooks(booksResult);
        setLastDoc(null);
        setHasMore(false);
      } catch (err) {
        console.error("Error searching books:", err);
        setError("Failed to search books");
      } finally {
        setLoading(false);
      }
    },
    [fetchBookRating, fetchBooks]
  );

  // Get unique categories
  const getCategories = useCallback(async () => {
    try {
      // Bounded scan for deriving categories
      const q = query(collection(db, "books"), limit(500));
      const querySnapshot = await getDocs(q);
      const categories = new Set<string>();

      querySnapshot.forEach((doc) => {
        const bookData = doc.data();
        if (bookData.category) {
          categories.add(bookData.category);
        }
      });

      return Array.from(categories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      return [];
    }
  }, []);

  // Get unique programs
  const getPrograms = useCallback(async () => {
    try {
      // Bounded scan for deriving programs
      const q = query(collection(db, "books"), limit(500));
      const querySnapshot = await getDocs(q);
      const programs = new Set<string>();

      querySnapshot.forEach((doc) => {
        const bookData = doc.data();
        if (bookData.program) {
          programs.add(bookData.program);
        }
      });

      return Array.from(programs);
    } catch (err) {
      console.error("Error fetching programs:", err);
      return [];
    }
  }, []);

  // Refresh books
  const refreshBooks = useCallback(() => {
    fetchBooks();
  }, [fetchBooks]);

  useEffect(() => {
    fetchBooks();
  }, []); // Empty dependency array since fetchBooks is now stable

  return {
    books,
    loading,
    error,
    hasMore,
    fetchBooks,
    loadMoreBooks,
    searchBooks,
    getCategories,
    getPrograms,
    refreshBooks,
  };
};
